import { useMemo } from 'react';

import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  from,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import merge from 'deepmerge';
import { IncomingHttpHeaders } from 'http';
import { isEqual } from 'lodash';
import logger from 'logger';
import { GetServerSideProps } from 'next';
import { getNextConfigValues } from 'next-config';

import { SimplifiedGraphError } from '../ts/interfaces/graph';
import { isServerSide } from './next';

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

let cachedApolloClient: ApolloClient | null = null;

/**
 * Generate only the _applicable and desired_ headers for Apollo requests
 * based on the incoming headers.
 *
 * Need to scope it down to just cookie and, when applicable, staging basic auth, otherwise
 * can get unexpected errors when deployed; see https://www.spektor.dev/apollo-client-and-ssl-handshake-failure/
 */
const generateHeaders = (
  headers?: IncomingHttpHeaders,
): Record<string, string> => {
  const {
    publicRuntimeConfig: { STAGING_HTTP_AUTH },
  } = getNextConfigValues();

  const finalHeaders = {} as Record<string, string>;
  if (headers?.cookie) {
    finalHeaders.cookie = headers.cookie;
  }
  if (STAGING_HTTP_AUTH) {
    finalHeaders.authorization = `Basic ${STAGING_HTTP_AUTH}`;
  }
  return finalHeaders;
};

/**
 * Initialize links with headers in order to pass auth cookie.
 */
const generateLink = (
  headers: IncomingHttpHeaders | undefined,
  uri: string,
) => {
  logger.debug({
    msg: 'generating apollo link',
    params: { uri, headers: generateHeaders(headers) },
  });

  return new HttpLink({
    uri,
    credentials: 'include',
    headers: generateHeaders(headers),
  });
};

/**
 * The various GraphQL services create clients to connect to. It's used
 * by ApolloClient to find the correct URI (aka URL).
 */
export const ClientNames = {
  CART_ORDER_GRAPH: 'cart-order-service',
  CATALOG_SERVICE_GRAPH: 'catalog-service',
  CATALOG_SERVICE_GRAPH_ABSOLUTE_URL: 'catalog-service-absolute-url',
};

/**
 * Handles errors so we can log some details as to what is going on
 */
const errorLink = onError(({ error, result }) => {
  // Check if there are GraphQL errors in the result
  if (result?.errors) {
    result.errors.forEach(({ message, locations, path }) =>
      logger.warn({
        msg: 'ApolloClient GraphQL error',
        params: { message, locations, path },
      }),
    );
  }

  // Log any other errors (network errors, etc.)
  if (error && !result?.errors) {
    logger.error({
      msg: 'ApolloClient error',
      noticable: error,
    });
  }
});

/**
 * Creates the Apollo client.
 *
 * What is a bit unique about this is how we use ApolloLinks to test and determine
 * what URL the request should be directed to. The `split` method includes a conditional and then another link
 * or another split. The result is that if provide a `clientName` in the context we get the correct URL.
 */
function createApolloClient(
  initialState: NormalizedCacheObject | undefined,
  headers: IncomingHttpHeaders | undefined,
  runningOnServer: boolean,
): ApolloClient {
  /**
   * Why are there server and client URLs? The reason is that when we make the request from the
   * server when want to use the "internal" DNS rather than the public domain and route back
   * through our API Gateway.
   */
  const {
    publicRuntimeConfig: { apiUrls },
    serverRuntimeConfig: { apiUrls: serverApiUrls },
  } = getNextConfigValues();

  // @todo-debt all this splittingn is kind of kooky; if we can't do schema stitching we should write a testable util
  // that takes graph configuration objects and does the splitting for us.
  return new ApolloClient({
    link: from([
      errorLink,
      ApolloLink.split(
        (operation) =>
          operation.getContext().clientName === ClientNames.CART_ORDER_GRAPH,
        generateLink(headers, apiUrls.cartOrderGraph),
        ApolloLink.split(
          (operation) =>
            operation.getContext().clientName ===
            ClientNames.CATALOG_SERVICE_GRAPH,
          generateLink(headers, apiUrls.catalogGraph),
          ApolloLink.split(
            (operation) =>
              operation.getContext().clientName ===
              ClientNames.CATALOG_SERVICE_GRAPH_ABSOLUTE_URL,
            generateLink(headers, process.env.ECOMM_CATALOG_INTERNAL_GRAPH!),
            generateLink(
              headers,
              runningOnServer ? serverApiUrls.railsGraph : apiUrls.railsGraph,
            ),
          ),
        ),
      ),
    ]),
    ssrMode: typeof window === 'undefined', // enable ssr mode if this is running on the server
    cache: new InMemoryCache().restore(initialState || {}),
  });
}

export function initializeApollo({
  initialState = undefined,
  headers = undefined,
}: {
  initialState?: NormalizedCacheObject;
  headers?: IncomingHttpHeaders;
}): ApolloClient {
  const runningOnServer = isServerSide();

  const apolloClient =
    cachedApolloClient ??
    createApolloClient(initialState, headers, runningOnServer);

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = apolloClient.extract() as NormalizedCacheObject;

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !isEqual(d, s)),
        ),
      ],
    });

    // Restore the cache with the merged data
    apolloClient.cache.restore(data);
  }
  // For SSG and SSR always create a new Apollo Client
  if (runningOnServer) {
    return apolloClient;
  }

  // Create the Apollo Client once in the client
  if (!cachedApolloClient) {
    cachedApolloClient = apolloClient;
  }

  return apolloClient;
}

// this is run in getSSP
// @todo determine if we should be using this... its not actually called anywhere currently,
// is it just cruft that should be deleted, or should we be using?
// export function addApolloState(
//   client: ApolloClient,
//   pageProps: AnyProps
// ): AnyProps {
//   const modifiedPageProps = { ...pageProps };

//   if (modifiedPageProps?.props) {
//     modifiedPageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
//   }

//   return modifiedPageProps;
// }

export function useApollo(pageProps: AnyProps): ApolloClient {
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  const store = useMemo(
    () => initializeApollo({ initialState: state }),
    [state],
  );
  return store;
}

export interface AnyProps {
  props:
    | GetServerSideProps
    | {
        hydrationError?: SimplifiedGraphError;
        [x: string]: any;
      };
  [APOLLO_STATE_PROP_NAME]?: NormalizedCacheObject;
}
